import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { Calendar, Settings, AlertCircle, CheckCircle } from 'lucide-react';

const CreateFiscalYearModal = ({ show, handleClose, handleSave, openFiscalYearsCount = 0 }) => {
    const currentYear = new Date().getFullYear();
    const defaultStartDate = `${currentYear}-01-01`;
    const defaultEndDate = `${currentYear}-12-31`;

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [accountingMethod, setAccountingMethod] = useState(null);
    const [vatPeriod, setVatPeriod] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Validation function
    const validateForm = () => {
        const newErrors = {};

        if (!startDate) {
            newErrors.startDate = 'Startdatum är obligatoriskt';
        }

        if (!endDate) {
            newErrors.endDate = 'Slutdatum är obligatoriskt';
        }

        if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
            newErrors.dateRange = 'Slutdatum måste vara efter startdatum';
        }

        if (!accountingMethod) {
            newErrors.accountingMethod = 'Bokföringsmetod är obligatorisk';
        }

        if (!vatPeriod) {
            newErrors.vatPeriod = 'Momsperiod är obligatorisk';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        const isValid = validateForm();
        setIsFormValid(isValid);
    }, [startDate, endDate, accountingMethod, vatPeriod]);

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            await handleSave({
                startDate,
                endDate,
                accountingMethod: accountingMethod?.value,
                vatPeriod: vatPeriod?.value
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        setAccountingMethod(null);
        setVatPeriod(null);
        setErrors({});
    };

    const accountingMethodOptions = [
        {
            value: 'accrual',
            label: 'Faktureringsmetoden',
            description: 'Intäkter och kostnader bokförs när de uppstår'
        },
        {
            value: 'cash',
            label: 'Kontantmetoden',
            description: 'Intäkter och kostnader bokförs när betalning sker'
        }
    ];

    const vatPeriodOptions = [
        {
            value: 'monthly',
            label: 'Månadsvis',
            description: 'Momsredovisning varje månad'
        },
        {
            value: 'quarterly',
            label: 'Kvartalsvis',
            description: 'Momsredovisning varje kvartal'
        },
        {
            value: 'yearly',
            label: 'Årsvis',
            description: 'Momsredovisning en gång per år'
        }
    ];

    // Calculate fiscal year period
    const getFiscalYearInfo = () => {
        if (!startDate || !endDate) return null;

        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        return {
            year: start.getFullYear(),
            days: diffDays,
            isStandardYear: diffDays >= 365 && diffDays <= 366
        };
    };

    const fiscalInfo = getFiscalYearInfo();

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            border: errors.accountingMethod || errors.vatPeriod ? '2px solid #dc3545' : '2px solid #e2e8f0',
            borderRadius: '8px',
            minHeight: '44px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? 'white' : '#1e293b',
            padding: '12px 16px'
        })
    };

    return (
        <>
            <style>{`
                .fiscal-year-modal .modal-content {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }
                
                .fiscal-year-modal .modal-header {
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border-bottom: 1px solid #e2e8f0;
                    border-radius: 16px 16px 0 0;
                    padding: 1.5rem 2rem;
                }
                
                .fiscal-year-modal .modal-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e293b;
                }
                
                .fiscal-year-modal .modal-body {
                    padding: 2rem;
                }
                
                .fiscal-year-modal .modal-footer {
                    border-top: 1px solid #e2e8f0;
                    padding: 1.5rem 2rem;
                    background: #f8fafc;
                    border-radius: 0 0 16px 16px;
                }
                
                .form-section {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }
                
                .form-section-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                    font-weight: 600;
                    color: #374151;
                }
                
                .form-control-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    transition: all 0.3s ease;
                    font-size: 0.875rem;
                }
                
                .form-control-modern:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                
                .form-control-modern.is-invalid {
                    border-color: #dc3545;
                }
                
                .form-label-modern {
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                }
                
                .info-card {
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border: 1px solid #93c5fd;
                    border-radius: 12px;
                    padding: 1rem;
                    margin-top: 1rem;
                }
                
                .info-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    color: #1e40af;
                    margin-bottom: 0.5rem;
                }
            `}</style>

            <Modal
                show={show}
                onHide={handleClose}
                className="fiscal-year-modal"
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Calendar size={24} />
                        Skapa nytt räkenskapsår
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {openFiscalYearsCount >= 2 && (
                        <Alert variant="warning" className="d-flex align-items-start gap-2 mb-3">
                            <AlertCircle size={18} className="flex-shrink-0 mt-1" />
                            <div>
                                <strong>Det finns redan {openFiscalYearsCount} öppna räkenskapsår.</strong>
                                <br />
                                Du måste stänga minst ett tidigare räkenskapsår innan du kan skapa ett nytt.
                            </div>
                        </Alert>
                    )}

                    {/* Date Period Section */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <Calendar size={18} />
                            Räkenskapsperiod
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Startdatum *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className={`form-control-modern ${errors.startDate ? 'is-invalid' : ''}`}
                                    />
                                    {errors.startDate && (
                                        <div className="invalid-feedback d-block">
                                            {errors.startDate}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Slutdatum *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className={`form-control-modern ${errors.endDate ? 'is-invalid' : ''}`}
                                    />
                                    {errors.endDate && (
                                        <div className="invalid-feedback d-block">
                                            {errors.endDate}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        {errors.dateRange && (
                            <Alert variant="danger" className="mt-2 mb-0">
                                <AlertCircle size={16} className="me-2" />
                                {errors.dateRange}
                            </Alert>
                        )}

                        {fiscalInfo && !errors.dateRange && (
                            <div className="info-card">
                                <div className="info-header">
                                    <CheckCircle size={16} />
                                    Räkenskapsårsinfo
                                </div>
                                <div className="small text-muted">
                                    <strong>År:</strong> {fiscalInfo.year} •
                                    <strong> Längd:</strong> {fiscalInfo.days} dagar
                                    {!fiscalInfo.isStandardYear && (
                                        <span className="text-warning ms-2">
                                            ⚠️ Ej standardår (365-366 dagar)
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Accounting Settings Section */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <Settings size={18} />
                            Bokföringsinställningar
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Bokföringsmetod *</Form.Label>
                                    <Select
                                        value={accountingMethod}
                                        onChange={setAccountingMethod}
                                        options={accountingMethodOptions}
                                        placeholder="Välj bokföringsmetod..."
                                        styles={customSelectStyles}
                                        formatOptionLabel={(option) => (
                                            <div>
                                                <div style={{ fontWeight: '500' }}>{option.label}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                    {option.description}
                                                </div>
                                            </div>
                                        )}
                                    />
                                    {errors.accountingMethod && (
                                        <div className="invalid-feedback d-block">
                                            {errors.accountingMethod}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Momsperiod *</Form.Label>
                                    <Select
                                        value={vatPeriod}
                                        onChange={setVatPeriod}
                                        options={vatPeriodOptions}
                                        placeholder="Välj momsperiod..."
                                        styles={customSelectStyles}
                                        formatOptionLabel={(option) => (
                                            <div>
                                                <div style={{ fontWeight: '500' }}>{option.label}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                    {option.description}
                                                </div>
                                            </div>
                                        )}
                                    />
                                    {errors.vatPeriod && (
                                        <div className="invalid-feedback d-block">
                                            {errors.vatPeriod}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between">
                    <Button
                        variant="outline-secondary"
                        onClick={handleReset}
                        className="d-flex align-items-center gap-2"
                    >
                        Återställ
                    </Button>

                    <div className="d-flex gap-2">
                        <Button
                            variant="secondary"
                            onClick={handleClose}
                        >
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!isFormValid || isLoading || openFiscalYearsCount >= 2}
                            className="d-flex align-items-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" />
                                    Sparar...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={16} />
                                    Skapa räkenskapsår
                                </>
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CreateFiscalYearModal;
